import type { CalendarEvent } from 'paintscout'
import React, { useEffect, useRef } from 'react'
import { makeStyles, Button, Typography, useMediaQuery, useTheme, type MuiTheme as Theme } from '@paintscout/ui'
import { EstimatorAvatar, useClientOptions, useDialogs, useUsers } from '@paintscout/ui-business'
import { removeAuth0Prefix } from '@paintscout/util'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import { goToDeal } from 'src/util/routing'
import isEmpty from 'lodash/isEmpty'
import { getQuoteTags } from '@paintscout/util/builder'
import { QuoteTagTooltip } from 'src/components/common/QuoteTagIcons'
import { getProjectTypeLabel } from './util'
import ProductionEventDialog from 'src/components/dialogs/EventDialog/ProductionEventDialog'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: '#ffffff',
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      transition: 'border-color 0.2s',
      overflow: 'hidden',
      cursor: 'grab',
      '&:active': {
        cursor: 'grabbing'
      },
      '&:hover': {
        borderColor: theme.paintscout.palette.primary
      },
      [theme.breakpoints.down('xs')]: {
        cursor: 'default',
        '&:active': {
          cursor: 'default'
        }
      }
    },
    section: {
      padding: theme.spacing(1.5, 2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5)
    },
    sectionDivider: {
      borderTop: `1px solid ${theme.palette.divider}`
    },
    headerRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      minWidth: 0
    },
    headerTitle: {
      color: 'rgb(37, 46, 61)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      minWidth: 0,
      '&:hover': {
        color: theme.paintscout.palette.primary
      }
    },
    dragHandle: {
      cursor: 'grab',
      color: '#64748b',
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      padding: theme.spacing(0.25),
      borderRadius: theme.shape.borderRadius,
      '&:hover': {
        backgroundColor: 'rgba(226, 232, 240, 0.6)',
        color: '#475569'
      },
      '&:active': {
        cursor: 'grabbing'
      }
    },
    dragIcon: {
      width: 20,
      height: 20,
      transform: 'rotate(90deg)'
    },
    subtitle: {
      color: 'rgb(115, 129, 152)',
      fontSize: '0.8125rem',
      lineHeight: 1.4,
      minHeight: '1.4em',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    moneyLine: {
      color: 'rgb(115, 129, 152)',
      fontSize: '0.8125rem'
    },
    soldByLine: {
      color: 'rgb(115, 129, 152)',
      fontSize: '0.8125rem',
      lineHeight: 1.4,
      minHeight: '1.4em'
    },
    footerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2)
    },
    footerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    avatarWrapper: {
      display: 'flex',
      alignItems: 'center',
      height: 26,
      lineHeight: 0
    },
    footerDivider: {
      width: 1,
      height: 18,
      backgroundColor: theme.palette.divider
    },
    tagChip: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 30,
      height: 26,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      color: 'rgb(115, 129, 152)',
      cursor: 'default',
      backgroundColor: '#f8fafc'
    },
    tagIcon: {
      width: 14,
      height: 14
    },
    scheduleButton: {
      color: 'rgb(115, 129, 152)',
      padding: theme.spacing(0.5, 1),
      minWidth: 0,
      gap: theme.spacing(0.5),
      '&:hover': {
        color: theme.paintscout.palette.primary,
        backgroundColor: 'transparent'
      }
    }
  }),
  { name: 'UnscheduledJobCard' }
)

function UnscheduledJobCard({
  event,
  onSchedule
}: {
  event?: CalendarEvent
  onSchedule: (eventId: string, startDate: number, endDate: number, crewId: string, memberIds: string[]) => void
}) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { users, userPictures } = useUsers()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const { openDialog, dismissDialog } = useDialogs()

  const eventDuration = event && event.deal?.hours ? Math.ceil(event.deal?.hours / 8) : 5

  const cardRef = useRef<HTMLDivElement>(null)
  const dragHandleRef = useRef<HTMLDivElement>(null)

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'event',
    item: { eventId: event.id, event, duration: eventDuration, isUnscheduled: true },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  // On mobile: only the handle is the drag source so card-body touches scroll the list.
  // On desktop: the whole card is draggable for a wider grab target.
  if (isMobile) {
    drag(dragHandleRef)
  } else {
    drag(cardRef)
  }

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const projectTypeLabel = getProjectTypeLabel(event.projectType, options)
  const subtitleParts = [event.address, projectTypeLabel].filter(Boolean)
  const soldByName = event.soldByEstimator?.name
  const tagOptions = getQuoteTags({ options })
  const fullTags = event.deal.tags ? tagOptions.filter((tagOption) => event.deal.tags.includes(tagOption.name)) : []
  const hasTags = !isEmpty(fullTags)
  // The bottom-left avatar represents the current deal owner. Until that field is wired
  // up separately, fall back to the quote's "sold by" estimator and then the deal's estimator.
  const ownerEntity = event.soldByEstimator || event.estimator
  const ownerUser = ownerEntity?.id
    ? users?.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(ownerEntity.id))
    : undefined
  const ownerAvatarUser = ownerEntity
    ? {
        id: ownerEntity.id,
        firstName: ownerUser?.user_metadata?.firstName ?? ownerEntity.name?.split(' ')[0] ?? '',
        lastName: ownerUser?.user_metadata?.lastName ?? ownerEntity.name?.split(' ').slice(1).join(' ') ?? ''
      }
    : null

  const handleScheduleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    openDialog(ProductionEventDialog, {
      isNew: true,
      event,
      onCancelEvent: dismissDialog,
      onConfirm: (confirmed) => {
        if (confirmed.start && confirmed.end) {
          const memberIds = Object.entries(confirmed.assignedUsers ?? {})
            .filter(([, v]) => v.assigned)
            .map(([id]) => id)
          onSchedule(confirmed.id, confirmed.start.getTime(), confirmed.end.getTime(), confirmed.crew ?? '', memberIds)
        }
        dismissDialog()
      }
    })
  }

  return (
    <>
      <div ref={cardRef} className={classes.root} style={{ opacity: isDragging ? 0.3 : 1 }}>
        <div className={classes.section}>
          <div className={classes.headerRow}>
            <Typography
              variant="h4"
              className={classes.headerTitle}
              weight={'bold'}
              onClick={(e) => {
                e.stopPropagation()
                goToDeal(event.deal.id)
              }}
            >
              {event.title}
            </Typography>
            <div ref={dragHandleRef} className={classes.dragHandle} title="Drag to schedule">
              <DragIndicatorIcon className={classes.dragIcon} />
            </div>
          </div>
          <div className={classes.subtitle}>{subtitleParts.length > 0 ? subtitleParts.join(' • ') : ' '}</div>
        </div>

        <div className={`${classes.section} ${classes.sectionDivider}`}>
          <div className={classes.moneyLine}>
            {formatCurrency(event.deal.value)} ({Math.round((event.deal?.hours || 0) * 100) / 100}h)
          </div>
          <div className={classes.soldByLine}>{soldByName ? `Sold by: ${soldByName}` : ' '}</div>
        </div>

        <div className={`${classes.footerRow} ${classes.sectionDivider}`}>
          <div className={classes.footerLeft}>
            {ownerAvatarUser && (
              <span className={classes.avatarWrapper}>
                <EstimatorAvatar
                  user={ownerAvatarUser}
                  userPictures={userPictures}
                  width="26px"
                  height="26px"
                  fontSize="11px"
                  borderColor={ownerUser?.user_metadata?.color}
                  hideTooltip={false}
                />
              </span>
            )}
            {ownerAvatarUser && hasTags && <span className={classes.footerDivider} aria-hidden />}
            <EventTagChip fullTags={fullTags} classes={classes} />
          </div>
          <Button
            variant="text"
            size="small"
            icon={CalendarTodayIcon}
            className={classes.scheduleButton}
            onClick={handleScheduleClick}
            aria-label="Schedule"
          >
            Schedule
          </Button>
        </div>
      </div>
    </>
  )
}

export default UnscheduledJobCard

function EventTagChip(args: { fullTags: ReturnType<typeof getQuoteTags>; classes: ReturnType<typeof useStyles> }) {
  const { fullTags, classes } = args

  if (isEmpty(fullTags)) return null

  return (
    <QuoteTagTooltip tags={fullTags}>
      <span className={classes.tagChip} aria-label={`${fullTags.length} tag${fullTags.length === 1 ? '' : 's'}`}>
        <LocalOfferOutlinedIcon className={classes.tagIcon} />
      </span>
    </QuoteTagTooltip>
  )
}
